import '.a2{position:absolute;}.ssr-plugin'
import '.bf{inset:0px;}.ssr-plugin'
import '.cs{opacity:0;}.ssr-plugin'
import '.q{cursor:pointer;}.ssr-plugin'
import { FC, useMemo, useState, SelectHTMLAttributes, FocusEvent } from 'react'
import { styled, Style } from 'inlines'
import { Text } from './Text'
import { useTheme } from '../providers/useTheme'
import { Button } from './Button'
import { Language } from '@/apps/cms/components/SiteLanguages'
import { useIsMobile } from '../hooks/useIsMobile'
import useLocalStorage from '@based/use-local-storage'

const SelectWrapper = styled('div', {
  position: 'relative',
  display: 'inline-flex',
  alignItems: 'center',
})

export type LanguageSelectorProps = Omit<
  SelectHTMLAttributes<HTMLSelectElement>,
  'value' | 'defaultValue' | 'onChange' | 'children'
> & {
  languages: Language[]
  value?: string
  defaultValue?: string
  style?: Style
  onChange?: (value: string) => void
}

export const LanguageSelector: FC<LanguageSelectorProps> = ({
  languages,
  value,
  defaultValue,
  onChange,
  'aria-label': ariaLabelProp,
  name,
  disabled,
  required,
  style,
  ...rest
}) => {
  const { colors } = useTheme()
  const isMobile = useIsMobile()
  const [storageActiveLanguage, setStorageActiveLanguage] = useLocalStorage(
    'language',
    {
      value,
    },
  )

  const [uncontrolledValue, setUncontrolledValue] = useState<string>(() => {
    if (value !== undefined) return value
    if (defaultValue !== undefined) return defaultValue

    return storageActiveLanguage ?? languages?.[0]?.value
  })

  const isControlled = value !== undefined
  const currentValue = isControlled ? value : uncontrolledValue

  const currentOptionLabel = useMemo(() => {
    const current = languages?.find((opt) => opt.value === currentValue)
    return current?.label ?? ''
  }, [languages, currentValue])

  const handleChange = (ev: React.ChangeEvent<HTMLSelectElement>) => {
    const nextValue = ev.target.value

    if (!isControlled) {
      setUncontrolledValue(nextValue)
    }

    setStorageActiveLanguage(nextValue)

    onChange?.(nextValue)
  }

  const handleFocus = (ev: FocusEvent<HTMLSelectElement>) => {
    rest.onFocus?.(ev)
  }

  const handleBlur = (ev: FocusEvent<HTMLSelectElement>) => {
    rest.onBlur?.(ev)
  }

  return (
    <SelectWrapper style={{ outline: 'none', ...style }}>
      <Button
        id="language-selector-button"
        isRaw
        leadIcon="language"
        trailIcon={isMobile ? 'chevron' : undefined}
        color={colors.textSecondary}
      >
        <Text variant="subheading-regular" color={colors.textSecondary}>
          {currentOptionLabel}
        </Text>
      </Button>

      <select className="a2 bf cs q"
        id="language-selector-select"
        name={name}
        value={currentValue}
        onChange={handleChange}
        aria-label={ariaLabelProp}
        disabled={disabled}
        required={required}
        onFocus={handleFocus}
        onBlur={handleBlur}
      >
        {languages?.map((opt) => (
          <option
            id={`language-selector-option-${opt.value.toLowerCase()}`}
            key={opt.value}
            value={opt.value}
          >
            {opt.label}
          </option>
        ))}
      </select>
    </SelectWrapper>
  )
}
